import { Box, Typography } from '@mui/material';
import { gridGapSx, tokens } from '../../theme';

/**
 * The two things a method page will hold and does not yet: skills, training.
 *
 * **Drawn, recessed, and not interactive — the placeholder rule, in its
 * clearest case.** Every word here is ours; there is no field behind any of it
 * and nothing to click. A dashed border on a `sunken` ground is what says
 * "this is an outline, not a thing", where a solid card that happened to be
 * empty would read as content that failed to load.
 *
 * **The recession is the container's, never the text's.** This used to fade
 * the whole panel to `opacity: 0.72`, which composited the caption and body
 * down to 2.70:1 — `text.disabled` strength, on real sentences, while every
 * token involved passed its own check. A ground and a border can recede as far
 * as you like; text cannot.
 *
 * It is the same on every method page, so it takes no props. When either
 * becomes real it stops being this component.
 */

const PANELS = [
  {
    heading: 'Skills',
    caption: 'Curated skill paths for this method',
    body: 'Coming soon — role-specific skill progressions and certifications.',
  },
  {
    heading: 'Training',
    caption: 'Learning resources and workshops',
    body: 'Coming soon — guided training modules, video walkthroughs, and practice exercises.',
  },
] as const;

export const ComingSoonRow = () => (
  <Box
    sx={{
      display: 'grid',
      // Three tracks for two panels, so each keeps the card width the page's
      // other rows draw rather than widening to fill the row.
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
      ...gridGapSx,
    }}
  >
    {PANELS.map((panel) => (
      <Box
        key={panel.heading}
        sx={{
          backgroundColor: 'surface.sunken',
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'border.default',
          borderRadius: `${String(tokens.radius.lg)}px`,
          padding: 6,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography variant="h4" component="h3">
          {panel.heading}
        </Typography>

        <Typography component="p" sx={{ fontSize: 11, lineHeight: 1.5, color: 'text.meta' }}>
          {panel.caption}
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {panel.body}
        </Typography>
      </Box>
    ))}
  </Box>
);
